import { useState } from "react";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { RepairStepRow, skipButton } from "./RepairStepRow";
import type { PlanStep, RepairState, TranscriptEntry } from "./repairReducer";
import { isPlanComplete } from "./repairReducer";
import type { RestoreStructureResult, StepId } from "../../api/repair";
import { RestoreStructureDetails } from "./RestoreStructureDetails";

type Props = {
  state: RepairState;
  onApply: (stepId: StepId) => Promise<void>;
  onSkip: (stepId: StepId) => void;
  onApplyAll: () => Promise<void>;
  onExportTranscript: () => void;
  onBack: () => void;
  onClose: () => void;
  onFinish: () => void;
};

function stepActionLabel(stepId: StepId): string {
  switch (stepId) {
    case "restore-missing":
      return "Restore";
    case "remove-external":
      return "Remove";
    case "restore-folders":
      return "Restore folders";
  }
}

export function ExecuteStep(props: Props) {
  const { state, onApply, onSkip, onApplyAll, onExportTranscript, onBack, onClose, onFinish } = props;
  // Previews are open by default. Admin can collapse a noisy one if they want.
  const [collapsedPreview, setCollapsedPreview] = useState<Record<string, boolean>>({});
  const complete = isPlanComplete(state.planSteps);

  if (state.phase === "done") {
    return <CompletionScreen transcript={state.transcript} onExport={onExportTranscript} onClose={onClose} />;
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Step 2 — Repair</h3>
        <p className="text-sm text-gray-600 mt-1">
          Each step shows exactly what it will change. Apply one at a time, or all at once.
        </p>
      </div>

      <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
        <div className="flex gap-2 flex-wrap items-center">
          <SudoButton variant="primary" size="default" onClick={onApplyAll} disabled={complete}>
            Apply all
          </SudoButton>
        </div>
        <p className="text-sm text-gray-700">
          Run everything at once with the button above, <span className="font-medium">or apply one step at a time using the buttons in the list below</span>.
        </p>
      </div>

      <div className="space-y-2">
        {state.planSteps.map((step, i) => {
          // Hide the restore-folders step entirely when no backup is picked —
          // it's an optional recovery path and showing "skipped (no backup)"
          // just clutters the flow. The backup picker in diagnose is the
          // single entry point for opting into that path.
          if (step.id === "restore-folders" && step.status === "skipped" && !step.result) return null;

          const previewOpen = !collapsedPreview[step.id];
          return (
            <div key={step.id}>
              <RepairStepRow
                step={step}
                stepIndex={i}
                showPreviewToggle
                previewOpen={previewOpen}
                onTogglePreview={() => setCollapsedPreview((prev) => ({ ...prev, [step.id]: !prev[step.id] }))}
                actions={<StepActions step={step} onApply={() => onApply(step.id)} onSkip={() => onSkip(step.id)} />}
              />
              {step.id === "restore-folders" && step.result && previewOpen && (
                <div className="ml-9 mt-2 pl-3 border-l-2 border-gray-200">
                  <RestoreStructureDetails result={step.result as RestoreStructureResult} diagnosis={state.diagnosis} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-between pt-2">
        <SudoButton variant="tertiary" size="default" onClick={onBack}>
          Back
        </SudoButton>
        {complete ? (
          <SudoButton variant="primary" size="default" onClick={onFinish}>
            View summary
          </SudoButton>
        ) : (
          <SudoButton variant="tertiary" size="default" onClick={onClose}>
            Close
          </SudoButton>
        )}
      </div>
    </div>
  );
}

function StepActions({ step, onApply, onSkip }: { step: PlanStep; onApply: () => void; onSkip: () => void }) {
  if (step.status === "complete") {
    return <span className="text-xs text-green-700 font-medium">Done</span>;
  }
  if (step.status === "skipped") {
    return <span className="text-xs text-gray-500 font-medium">Skipped</span>;
  }
  if (step.status === "in-progress") {
    return <span className="text-xs text-blue-700 font-medium">Working…</span>;
  }
  if (step.status === "failed") {
    return (
      <div className="flex flex-col gap-1">
        <SudoButton variant="primary" size="small" onClick={onApply}>
          Retry
        </SudoButton>
        {skipButton({ step, onSkip })}
      </div>
    );
  }
  if (step.status === "ready") {
    return (
      <div className="flex flex-col gap-1">
        <SudoButton variant="primary" size="small" onClick={onApply}>
          {stepActionLabel(step.id)}
        </SudoButton>
        {skipButton({ step, onSkip })}
      </div>
    );
  }
  // pending — waiting for an earlier step to finish
  return (
    <div className="flex flex-col gap-1">
      <SudoButton variant="primary" size="small" disabled>
        {stepActionLabel(step.id)}
      </SudoButton>
      {skipButton({ step, onSkip })}
    </div>
  );
}

function CompletionScreen({ transcript, onExport, onClose }: { transcript: TranscriptEntry[]; onExport: () => void; onClose: () => void }) {
  const totalChanges = transcript.reduce((sum, t) => sum + t.count, 0);

  return (
    <div className="space-y-4">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-lg font-medium text-green-900">All clean</p>
        <p className="text-sm text-green-800 mt-1">
          {transcript.length === 0
            ? "Nothing needed to be repaired."
            : `${transcript.length} step${transcript.length === 1 ? "" : "s"} applied · ${totalChanges} change${totalChanges === 1 ? "" : "s"} committed.`}
        </p>
      </div>

      {transcript.length > 0 && (
        <div className="border rounded-lg">
          <div className="px-3 py-2 bg-gray-50 border-b text-sm font-medium text-gray-700">Transcript</div>
          <ul className="divide-y">
            {transcript.map((entry, i) => (
              <li key={i} className="px-3 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{entry.title}</span>
                  <span className="text-xs text-gray-500">{new Date(entry.at).toLocaleTimeString()}</span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5">{entry.count} change{entry.count === 1 ? "" : "s"}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end gap-2">
        {transcript.length > 0 && (
          <SudoButton variant="tertiary" size="default" onClick={onExport}>
            Export log
          </SudoButton>
        )}
        <SudoButton variant="primary" size="default" onClick={onClose}>
          Close
        </SudoButton>
      </div>
    </div>
  );
}
